/**
 * Invitations to other people's apps (Teams), at the top of the projects list.
 * This is where an invitee who signed in AFTER following the emailed link
 * finds it: sign-in has no return path, so the link's own screen is not the
 * only way in, and the list keys off the signed-in email rather than a token.
 *
 * Renders nothing when there is nothing waiting, which is almost always.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { Button } from '../primitives/Button';
import { Notice } from './Notice';

import { acceptInvitation, declineInvitation, listMyInvitations, type MyInvitation } from '../../lib/real-api';
import { captureEvent } from '../../lib/analytics/posthog';
import { ApiError } from '../../lib/http';
import { useAsync } from '../../lib/hooks/use-async';
import { t } from '../../lib/i18n/t';

export function PendingInvitations() {
  const invites = useAsync(listMyInvitations, []);
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const waiting = invites.data ?? [];

  useEffect(() => {
    if (waiting.length > 0) {
      captureEvent('project_invitations_shown', { count: waiting.length });
    }
  }, [waiting.length]);

  if (waiting.length === 0) return null;

  async function answer(invitation: MyInvitation, accept: boolean): Promise<void> {
    setBusy(true);
    setError(null);
    captureEvent(accept ? 'project_invite_accept_clicked' : 'project_invite_decline_clicked', {
      project_slug: invitation.projectSlug,
      source: 'projects_list',
    });
    try {
      if (accept) {
        const { projectSlug } = await acceptInvitation(invitation.id);
        captureEvent('project_invite_accepted', { project_slug: projectSlug, source: 'projects_list' });
        navigate(`/p/${encodeURIComponent(projectSlug)}`);
        return;
      }
      await declineInvitation(invitation.id);
      invites.refetch();
    } catch (err: unknown) {
      const code = err instanceof ApiError ? err.code : 'unknown';
      captureEvent('project_invite_answer_failed', { error_code: code, source: 'projects_list' });
      setError(err instanceof ApiError ? err.message : t('That did not go through. Try again.'));
      invites.refetch();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="pl__invites">
      {error ? <Notice tone="attention">{error}</Notice> : null}
      {waiting.map((invitation) => (
        <Notice key={invitation.id} tone="quiet">
          <p>
            {invitation.invitedBy
              ? t('{person} invited you to work on {project}.', {
                  person: invitation.invitedBy,
                  project: invitation.projectName,
                })
              : t('You were invited to work on {project}.', { project: invitation.projectName })}
          </p>
          <div className="set__actions">
            <Button variant="solid" disabled={busy} onClick={() => answer(invitation, true)}>
              {t('Accept')}
            </Button>
            <Button variant="outline" disabled={busy} onClick={() => answer(invitation, false)}>
              {t('Decline')}
            </Button>
          </div>
        </Notice>
      ))}
    </div>
  );
}
